import DaySchedule from "views/multiposting/DaySchedule";
import PauseIcon from "assets/icons/PauseIcon";
import ArrowLeftIcon from "icons/ArrowLeftIcon";
import PlayIcon from "assets/icons/play-icon-tiktok.svg";
import { capitalizeFirstLetter, showAlert } from "utils/utils";
import Spacer from "components/global/Spacer";
import { socialIcons } from "constants/post";
import Button from "components/global/Button";
import Loader from "components/global/Loader";
import { useEffect, useMemo, useReducer, useState } from "react";
import {
  getSingleQueueSettings,
  pauseSelectedPlatformQueue,
  updateQueueSettings,
} from "api";
import useTimezone from "hooks/useTimezone";
import ConfirmModal from "components/global/ConfirmModal";
import styled from "styled-components";
import { useLocation, useHistory } from "react-router-dom";
import { connect } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";

const ConfigPage = styled.div`
  background-color: var(--background);
  border-left: 1px solid var(--grey_10);
  width: 100%;
  position: relative;
  height: calc(100vh - 12.9rem);

  .time-slots {
    gap: 24px;
    margin-bottom: 48px;

    .slot-row {
      gap: 6px;
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(70px, 1fr));
    }

    .slot {
      padding: 4px;
      width: 100%;
      border: 1px solid var(--grey_10);
      border-radius: 8px;
      background-color: var(--white);
    }

    .disabled {
      opacity: 0.5;
    }
  }
  .disabled {
    opacity: 0.5;
    pointer-events: none;
  }

  .content {
    height: calc(100% - 72px);
    overflow: auto;
    padding: 24px 12px;
  }

  .page-title_container {
    margin-bottom: 24px;
    gap: 12px;
    display: none;
  }

  .footer {
    position: absolute;
    bottom: 0;
    width: 100%;
    padding: 12px 12px;
    background-color: #fff;
    height: 72px;
    display: flex;
    justify-content: space-between;
    gap: 12px;
    right: 0;

    button {
      font-size: 14px;
      font-style: normal;
      font-weight: 450;
    }
  }

  @media screen and (max-width: 768px) {
    height: calc(100vh - 14.4rem);

    .page-title_container {
      margin-bottom: 24px;
      gap: 12px;
      display: flex;
    }
  }
`;

const days = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

const queueScheduleInitState = {
  selectedDays: [],
  daySchedule: [],
};

const queueScheduleReducer = (state, action) => {
  return {
    ...state,
    daySchedule: action.payload,
  };
};

const QueueConfig = ({ channel, brand }) => {
  const { search } = useLocation();
  const history = useHistory();
  const url_platform = new URLSearchParams(search).get("platform");
  const queryClient = useQueryClient();

  const { getTimezoneGMT } = useTimezone();
  const [loadingQueue, setLoadingQueue] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [platformQueueSetting, setPlatformQueueSetting] = useState([]);
  const [queueSchedule, queueScheduleDispatch] = useReducer(
    queueScheduleReducer,
    queueScheduleInitState
  );
  const platform = channel ?? url_platform;

  /**
   * Returns true if there is a day with an active queue status
   * else returns false
   */
  const hasActiveStatus = useMemo(() => {
    let statusList = [];

    platformQueueSetting?.forEach((obj) => {
      if (obj.status === 1) {
        statusList.push(true);
      } else {
        statusList.push(false);
      }
    });
    return statusList.includes(true);
  }, [platformQueueSetting]);

  const getQueueSettings = async () => {
    if (!brand || !platform) return;

    setLoadingQueue(true);
    try {
      const res = await getSingleQueueSettings(platform);
      if (res.data.status === true) {
        setPlatformQueueSetting(res.data?.data?.queue_settings);
      } else {
        showAlert({
          type: "danger",
          msg: res.data?.message,
        });
      }
      setLoadingQueue(false);
    } catch (error) {
      setLoadingQueue(false);
      showAlert({
        type: "danger",
        msg:
          error.response?.data?.message ||
          "An error occurred while fetching queue",
      });
    }
  };

  const tempDaySchedule = useMemo(() => {
    const schedule = platformQueueSetting.map((item) => {
      return {
        id: item.id,
        day: item.day,
        status: item.status === 1 ? true : false,
        slots: item.slots.map((slot) => {
          let parts = slot.split(" ");

          let timeParts = parts[0].split(":");
          let hour = timeParts[0];
          let minute = timeParts[1];
          let timeOfDay = parts[1];
          return {
            hour,
            minute,
            timeOfDay,
          };
        }),
      };
    });
    return schedule;
  }, [platformQueueSetting]);

  //handle save queued post time slots
  const handleSaveTimeSlot = async () => {
    setIsSubmitting(true);

    const schedules = [
      {
        platform: platform,
        days: queueSchedule?.daySchedule?.map((schedule) => {
          // get the ID of the day from the platform initial settings
          const platformDayId = platformQueueSetting.find(
            (setting) => setting.day === schedule.day
          ).id;

          return {
            id: platformDayId,
            slots: schedule.slots.map(
              (slot) => slot.hour + ":" + slot.minute + " " + slot.timeOfDay
            ),
            status: schedule?.status === true ? 1 : 3,
          };
        }),
      },
    ];
    try {
      const submitData = {
        queue_settings: schedules,
      };
      const res = await updateQueueSettings(submitData);
      console.log({ res });
      if (res.data?.status === true) {
        getQueueSettings();
        showAlert({
          type: "success",
          msg: "Changes saved",
        });
      }
      queryClient.invalidateQueries({ queryKey: ["queuedPost", platform] });
      setIsSubmitting(false);
    } catch (error) {
      setIsSubmitting(false);
      showAlert({
        type: "danger",
        msg: error.response?.data?.message || "An error occurred",
      });
    }
  };

  // toggle queue status
  const toggleQueueStatus = async () => {
    const action = hasActiveStatus ? "pause" : "resume";
    const data = {
      platforms: [platform],
    };
    setIsSubmitting(true);
    try {
      await pauseSelectedPlatformQueue(action, data);
      showAlert({
        type: "success",
        msg: "Changes saved",
      });
      await getQueueSettings();
    } catch (error) {
      showAlert({
        type: "danger",
        msg: error.response?.data?.message || "An error occurred",
      });
      setIsSubmitting(false);
    }
  };

  const handleResetTimeSlot = () => {
    //@ts-ignore
    queueScheduleDispatch({ type: "daySchedule", payload: tempDaySchedule });
  };

  useEffect(() => {
    getQueueSettings();
    // eslint-disable-next-line
  }, [platform]);

  useEffect(() => {
    queueScheduleDispatch({ type: "daySchedule", payload: tempDaySchedule });
  }, [tempDaySchedule]);

  return (
    <>
      <ConfirmModal
        id="pauseQueuedPost"
        title="Pause queue"
        noHeaderImages={true}
        type="info"
        description="Your queue will be paused, and all your scheduled posts will not go out until you resume your queue. "
        actionText="Pause"
        callback={toggleQueueStatus}
      />

      <ConfirmModal
        id="resumeQueuedPost"
        title="Resume queue"
        noHeaderImages={true}
        type="info"
        description="Your queue will be resumed, and all posts on your queue will begin to go out based on your posting schedule."
        actionText="Resume"
        callback={toggleQueueStatus}
      />
      <ConfigPage>
        <div className="content">
          <div className="flexRow page-title_container">
            <button
              className="flexRow alignCenter justifyCenter"
              onClick={() => history.goBack()}
            >
              <ArrowLeftIcon />
            </button>
            Queue settings
          </div>

          <div className="flexRow justifySpaceBetween header">
            {platform && (
              <div className="flexCol">
                <p
                  className="l3 bold textNoWrap flexRow alignCenter"
                  style={{ gap: "4px", marginBottom: "5px" }}
                >
                  <img src={socialIcons.circle[platform]} alt="." />
                  {capitalizeFirstLetter(platform)} timeslots
                </p>

                <p className="colorBodyText l4">
                  {getTimezoneGMT(brand?.timezone)}
                </p>
              </div>
            )}
            <Button
              height="38px"
              text={hasActiveStatus ? "Pause queue" : "Resume queue"}
              className="borderedGrey"
              icon={!hasActiveStatus && PlayIcon}
              iconComponent={hasActiveStatus && <PauseIcon />}
              onClick={() =>
                document
                  .querySelector(
                    hasActiveStatus ? "#pauseQueuedPost" : "#resumeQueuedPost"
                  )
                  .classList.add("show")
              }
            />
          </div>
          <Spacer y={2.4} />
          <div className="flexColumn time-slots">
            {days?.map((day, idx) => (
              <DaySchedule
                key={day}
                day={day}
                postSchedule={queueSchedule}
                postScheduleDispatch={queueScheduleDispatch}
              />
            ))}
          </div>
        </div>

        <footer className="flexRow justifySpaceBetween alignCenter footer">
          <Button
            text="Cancel"
            className="cancel-btn borderedGrey"
            height="40px"
            width="100%"
            onClick={handleResetTimeSlot}
            disabled={isSubmitting}
          />
          <Button
            text="Save changes"
            className="save-btn"
            height="40px"
            width="100%"
            onClick={handleSaveTimeSlot}
            disabled={isSubmitting}
          />
        </footer>
      </ConfigPage>
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    brand: state.brand,
  };
};

export default connect(mapStateToProps)(QueueConfig);
